import { useDraggable } from '@dnd-kit/core';
import { DotsSixVertical } from '@phosphor-icons/react';
import { CredentialListItem } from '../../CredentialListItem';
import type { TestUserCredential } from './types';

type Props = {
  propertyId: string;
  credential: TestUserCredential;
  /** Persona (template) id of the containing group, or null when unassigned. */
  sourceTemplateId: string | null;
  /** Stagger index for the row entrance animation. */
  index?: number;
  onEdit: () => void;
  onDelete: () => void;
  onClearStorageState?: () => void;
  onUnlink?: () => void;
  onRefresh: () => void;
};

/**
 * Credential row that can be dragged between persona groups. Ephemeral
 * credentials are not draggable — they're transient sandbox artifacts and
 * never bind to a persona.
 */
export function DraggableCredentialRow({
  propertyId,
  credential,
  sourceTemplateId,
  index = 0,
  onEdit,
  onDelete,
  onClearStorageState,
  onUnlink,
  onRefresh,
}: Props) {
  const draggable = !credential.isEphemeral;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `cred:${credential.id}`,
    data: { credentialId: credential.id, sourceTemplateId },
    disabled: !draggable,
  });

  return (
    <div ref={setNodeRef} className={isDragging ? 'opacity-40' : undefined}>
      <CredentialListItem
        propertyId={propertyId}
        credential={credential}
        index={index}
        leading={
          draggable ? (
            <button
              type="button"
              className="flex items-center -ml-2 px-1 text-muted-foreground/60 hover:text-foreground cursor-grab active:cursor-grabbing touch-none shrink-0"
              aria-label={`Drag ${credential.name} to a persona`}
              {...attributes}
              {...listeners}
            >
              <DotsSixVertical size={16} weight="bold" />
            </button>
          ) : undefined
        }
        onEdit={onEdit}
        onDelete={onDelete}
        onClearStorageState={onClearStorageState}
        onUnlink={onUnlink}
        onRefresh={onRefresh}
        variant="row"
      />
    </div>
  );
}
